import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { getOriginFromPath } from '@xing-com/crate-activity-center-module';
import { XingletLoader } from '@xing-com/crate-xinglet';
import { useActivityCenter } from '@xing-com/hub';
import { ModalPage } from '@xing-com/modal-page';
import { usePopOver } from '@xing-com/pop-over';
import { Portal } from '@xing-com/portal';
import { SuperEllipse } from '@xing-com/super-ellipse';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';

import {
  StyledBodyCopy,
  StyledMenu,
  StyledPopOver,
  StyledPopOverContentContainer,
  StyledPopOverTextContainer,
  SuperEllipseWrapper,
} from './wrapper.styled';

type WrapperProps = {
  activityCenterProps?: Record<string, unknown>;
};

const shouldOpenWrapper = (
  shouldOpenExternal: boolean,
  isActivityCenterVisible: boolean,
  isCheckoutPage: boolean
): boolean => {
  if (isCheckoutPage) {
    return false;
  }
  return shouldOpenExternal || isActivityCenterVisible;
};

export const Wrapper: React.FC<WrapperProps> = ({ activityCenterProps }) => {
  const {
    isActivityCenterVisible,
    openActivityCenter,
    closeActivityCenter,
    shouldOpenExternal,
    setShouldOpenExternal,
  } = useActivityCenter();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPopOverVisible, setIsPopOverVisible] = useState(false);
  const popOver = usePopOver();
  const isMobile = BREAKPOINTS.handheld === useMediaListener();
  const { formatMessage } = useIntl();
  const origin = getOriginFromPath();
  const isCheckoutPage = origin === 'checkout_page';

  useEffect(() => {
    if (shouldOpenExternal && isMobile) {
      setIsPopOverVisible(true);
      setIsAnimating(true);
      setTimeout(() => {
        setShouldOpenExternal(false);
      }, 3000);
    } else {
      setIsPopOverVisible(false);
      setTimeout(() => {
        setIsAnimating(false);
      }, 200);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [shouldOpenExternal]);

  useEffect(() => {
    if (isAnimating) {
      popOver.handleShow();
    } else {
      popOver.handleHide();
    }
  }, [isAnimating]);

  if (isMobile) {
    return (
      <>
        <StyledPopOver
          className="position-override"
          onOutsideClick={() => {
            setIsPopOverVisible(false);
            setTimeout(() => {
              popOver.handleHide();
            }, 300);
          }}
          show={popOver.show}
          $isVisible={isPopOverVisible}
        >
          <StyledPopOverContentContainer>
            <SuperEllipseWrapper>
              <SuperEllipse type="avatar" size="small" />
            </SuperEllipseWrapper>
            <StyledPopOverTextContainer>
              <StyledBodyCopy size="medium">
                {formatMessage({
                  id: 'ACTIVITY_CENTER_NOTIFICATION_MESSAGE',
                })}
              </StyledBodyCopy>
              <TextButton onClick={() => openActivityCenter('notification')}>
                {formatMessage({
                  id: 'ACTIVITY_CENTER_NOTIFICATION_OPEN',
                })}
              </TextButton>
            </StyledPopOverTextContainer>
          </StyledPopOverContentContainer>
        </StyledPopOver>
        {isActivityCenterVisible ? (
          <ModalPage
            closeButton={{
              onClick: () => {
                closeActivityCenter();
              },
            }}
            noGrid
            noMargin
            isAnimated
            isShown
          >
            <XingletLoader
              name="@xing-com/crate-activity-center-component"
              {...activityCenterProps}
            />
          </ModalPage>
        ) : null}
      </>
    );
  }
  return (
    <Portal containerId="activity-center-button">
      <StyledMenu
        dimmerTitle="close activity center"
        onOutsideClick={() => {
          closeActivityCenter();
          setShouldOpenExternal(false);
        }}
        show={shouldOpenWrapper(
          shouldOpenExternal,
          isActivityCenterVisible,
          isCheckoutPage
        )}
        isStatic
        isInFlow
        data-qa="activity-center-menu"
      >
        <XingletLoader
          name="@xing-com/crate-activity-center-component"
          {...activityCenterProps}
        />
      </StyledMenu>
    </Portal>
  );
};
